/**
 * author : Ananthapadmanabhan V K
 */
import React, { useContext, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import QuestionService from "../../../core/service/QuestionService";
import { toast } from "react-toastify";
import { Breadcrumb, Button, Form, Modal, OverlayTrigger, Table } from "react-bootstrap";
import Utils from "../../../core/utilities/Utils";
import { BiPlusCircle, BiTransfer, BiTrash } from "react-icons/bi";
import LanguageSelectorService from "../../../core/service/LanguageSelectorService";
import { LoaderContext } from "../../../App";
const LanguageSelector = ({ t, fieldLabel, convertedData, localizationData }) => {
    const [primaryLanguageCode, setPrimaryLanguageCode] = useState();
    const { setLoaderDisplay } = useContext(LoaderContext);
    const localizationContent = {
        fieldName: 'name',
        languageCode: "",
        translatedValue: ""
    }
    const [newData, setNewData] = useState([localizationContent]);
    const [languageOptions, setLanguageOptions] = useState([]);

    useEffect(() => {
        loadLanguages();
    }, []);

    const loadLanguages = () => {
        LanguageSelectorService.getLanguages().then(res => {
            setLanguageOptions(res.data);
            const primaryCode = res.data.find(language => language.primary)?.code;
            setPrimaryLanguageCode(primaryCode);
            if (localizationData && Array.isArray(localizationData) && localizationData.length > 0) {
                if (localizationData.length == 1) {
                    if (localizationData[0].languageCode == "") {
                        localizationData[0].languageCode = primaryCode
                    }
                }
                setNewData(handleChangeLanguageCodeToLowercase(localizationData, primaryCode));
            };
            if (!(localizationData && Array.isArray(localizationData) && localizationData.length > 0)) {
                setNewData(prevData =>
                    prevData.map(item => ({
                        ...item,
                        languageCode: primaryCode
                    }))
                );
            } else {
                convertedData(localizationData, getTranslatedValuesForDefaultLanguage(localizationData, primaryCode));
            }
        }).catch(error => {
            setLoaderDisplay(false);
        });
    }

    // change language code to lower case & order the object as the primary lang code will come first
    const handleChangeLanguageCodeToLowercase = (data, primaryCode) => {
        const updatedData = data
            .map(item => ({
                ...item,
                languageCode: item.languageCode.toLowerCase()
            }))
            .sort((a, b) => {
                if (a.languageCode == primaryCode) return -1;
                if (b.languageCode == primaryCode) return 1;
                return 0;
            });
        return updatedData;
    };

    const handleLocalizationChange = (index, field, value) => {
        const updatedItems = [...newData];
        updatedItems[index] = {
            ...updatedItems[index],
            [field]: value
        };
        setNewData(updatedItems);
        convertedData(updatedItems, getTranslatedValuesForDefaultLanguage(updatedItems, primaryLanguageCode));
    };

    const getTranslatedValuesForDefaultLanguage = (data, languageCode) => {
        const tVal = data
            .filter(item => item.languageCode == languageCode)
            .map(item => item.translatedValue);
        return tVal[0];
    };

    const getAvailableOptions = (code) => {
        const selectedValuesSet = new Set(newData.map(row => row.languageCode).filter(Boolean));
        const filtered = languageOptions.filter(
            option => {
                return !selectedValuesSet.has(option.code) ||
                    newData.find(row => row.languageCode === code)?.languageCode === option.code;
            }

        );
        return filtered;
    };

    const handleTranslate = (targetLang, index) => {
        const inputText = newData[0]?.translatedValue;
        const sourceLang = newData[0]?.languageCode;
        if (inputText != null && inputText != ''
            && sourceLang != null && sourceLang != ''
            && targetLang != null && targetLang != '') {
            QuestionService.translateText(inputText, sourceLang, targetLang).then(res => {
                if (res.status !== 200) return;
                const result = res.data.translations;
                const updatedItems = [...newData];
                updatedItems[index] = {
                    ...updatedItems[index],
                    translatedValue: result[0]?.text
                };
                setNewData(updatedItems);
                convertedData(updatedItems, getTranslatedValuesForDefaultLanguage(updatedItems, primaryLanguageCode));
            }).catch(err => { });
        } else {
            let message = '';
            if (sourceLang == '') {
                message = t("ProvideSourceMsg.label");
            } else if (targetLang == '') {
                message = t("ProvideTargetMsg.label");
            } else if (inputText == '') {
                message = t("ProvideTextMsg.label");
            }
            toast.warning(message, {
                position: toast.POSITION.TOP_RIGHT,
            });
        }

    }

    const addLocalizationRow = () => {
        setNewData(prevItems => [...prevItems, localizationContent]);
    };

    const deleteLocalizationQn = (index) => {
        const filtered = newData.filter((_, i) => i !== index);
        setNewData(filtered);
        convertedData(filtered, getTranslatedValuesForDefaultLanguage(filtered, primaryLanguageCode));
    };

    return (
        <>
            <div className="p-0 language-selector-card">
                <table className="table">
                    <tbody>
                        <tr>
                            <td>
                                <label className='mb-0'>{t("Language.label")} <span className="text-danger">*</span></label>
                            </td>
                            <td>
                                <label className='mb-0'>{fieldLabel} <span className="text-danger">*</span></label>
                            </td>
                            <td></td>
                        </tr>
                        {newData.map((item, index) => (<tr key={index}>
                            <td>
                                <select className="form-select" value={item.languageCode}
                                    disabled={item.languageCode == primaryLanguageCode}
                                    onChange={(e) => handleLocalizationChange(index, "languageCode", e.target.value)}>
                                    <option value="">{t("SelectLanguage.label")}</option>
                                    {getAvailableOptions(item.languageCode).map(option => (
                                        <option value={option.code} key={option.code}>
                                            {option.name}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td>
                                <input
                                    className="form-control"
                                    type="text"
                                    placeholder="Text"
                                    value={item.translatedValue}
                                    onChange={(e) => handleLocalizationChange(index, "translatedValue", e.target.value)}
                                />
                            </td>
                            <td>
                                <ul className='stakeCat_add'>
                                    {
                                        (index > 0) && (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={Utils.renderTooltip(
                                                    'Click to Translate'
                                                )}>
                                                <li>
                                                    <span className='cursor' onClick={() => handleTranslate(item.languageCode, index)}>
                                                        <BiTransfer />
                                                    </span>
                                                </li>
                                            </OverlayTrigger>
                                        )
                                    }
                                    {
                                        (newData.length > 1 && (item.languageCode != primaryLanguageCode)) && (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={Utils.renderTooltip(
                                                    'Delete'
                                                )}>
                                                <li>
                                                    <span className='cursor' onClick={() => deleteLocalizationQn(index)}>
                                                        <BiTrash />
                                                    </span>
                                                </li>
                                            </OverlayTrigger>
                                        )}
                                    {
                                        ((index == newData.length - 1) && newData.length < languageOptions.length) && (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={Utils.renderTooltip(
                                                    'Add new'
                                                )}>
                                                <li>
                                                    <Button variant='GroupStake' className='btn-outline-primary border-0' onClick={() => addLocalizationRow()}> <BiPlusCircle /></Button>
                                                </li>
                                            </OverlayTrigger>
                                        )}
                                </ul>
                            </td>
                        </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default withTranslation()(LanguageSelector);